import { ExpandableSection } from '@amzn/awsui-components-react';
import React, { ReactNode, useState } from 'react';

export interface AccordionItem {
  key: string;
  headerText: ReactNode;
  headerDescription?: string;
  headerCounter?: string;
  headerInfo?: ReactNode;
  headerActions?: ReactNode;
  content: ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
  allowMultiple?: boolean;
  defaultExpandedKey?: string;
  'data-testid'?: string;
}

/**
 * Stacked Cloudscape ExpandableSections that track their own open state. Opening a section closes the others
 * unless allowMultiple is set.
 */
const Accordion: React.FC<AccordionProps> = ({
  items,
  allowMultiple = false,
  defaultExpandedKey,
  'data-testid': dataTestId,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<string[]>(defaultExpandedKey ? [defaultExpandedKey] : []);

  const handleChange = (key: string, expanded: boolean) => {
    if (!expanded) {
      setExpandedKeys(expandedKeys.filter((expandedKey) => expandedKey !== key));
      return;
    }

    if (!allowMultiple) {
      setExpandedKeys([key]);
      return;
    }

    setExpandedKeys([...expandedKeys, key]);
  };

  return (
    <div data-testid={dataTestId}>
      {items.map((item) => (
        <ExpandableSection
          key={item.key}
          variant="stacked"
          expanded={expandedKeys.includes(item.key)}
          onChange={({ detail }) => handleChange(item.key, detail.expanded)}
          headerText={item.headerText}
          headerDescription={item.headerDescription}
          headerCounter={item.headerCounter}
          headerInfo={item.headerInfo}
          headerActions={item.headerActions}
        >
          {item.content}
        </ExpandableSection>
      ))}
    </div>
  );
};

export default Accordion;
